import React from "react";
import { UseFormSetValue, FieldErrors } from "react-hook-form";
import { CheckCircle, ExternalLink, FileText, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { UPLOAD_COPY } from "../../constants/messages";
import { FileUploadButton } from "../../components/file-upload-button";
import { type UpdateGamificationFormData } from "../../validations/gamification.validation";

const PDF_ACCEPT = ".pdf";

const REPLACE_CLASS =
  "h-auto self-auto gap-1 border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-primary hover:bg-white hover:text-primary";

const DROPZONE_CLASS =
  "group h-auto w-full flex-col gap-2 whitespace-normal rounded-xl border-2 border-dashed border-gray-300 bg-gradient-to-b from-gray-50 to-white px-6 py-8 text-center font-normal text-gray-600 hover:border-primary hover:bg-primary/5 hover:text-primary";

interface RegulationsCardProps {
  setValue: UseFormSetValue<UpdateGamificationFormData>;
  errors: FieldErrors<UpdateGamificationFormData>;
  hasRegulationFile: boolean;
  regulationsUrl?: string;
}

export const RegulationsCard: React.FC<RegulationsCardProps> = ({
  setValue,
  errors,
  hasRegulationFile,
  regulationsUrl,
}) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    setValue("regulations", file, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </span>
          <div>
            <CardTitle>Regulamento</CardTitle>
            <p className="text-sm text-muted-foreground">
              Documento em PDF com as regras oficiais da campanha.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {hasRegulationFile ? (
          <div className="flex flex-col gap-3 rounded-xl border border-green-200 bg-green-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
                <CheckCircle className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Regulamento anexado
                </p>
                <p className="text-xs text-green-600">
                  Esta campanha já possui um arquivo de regulamento.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {regulationsUrl && (
                <a
                  href={regulationsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Visualizar atual
                </a>
              )}
              <FileUploadButton
                inputId="regulations"
                accept={PDF_ACCEPT}
                onFileChange={handleFileChange}
                variant="outline"
                className={REPLACE_CLASS}
                aria-label={UPLOAD_COPY.replaceRegulation}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Substituir
              </FileUploadButton>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <FileUploadButton
              inputId="regulations"
              accept={PDF_ACCEPT}
              onFileChange={handleFileChange}
              variant="outline"
              className={DROPZONE_CLASS}
              aria-label={UPLOAD_COPY.chooseRegulation}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors group-hover:bg-primary/10 group-hover:text-primary">
                <FileText className="h-6 w-6" aria-hidden="true" />
              </span>
              <span className="text-sm font-medium">
                Clique para enviar o regulamento
              </span>
              <span className="text-xs text-gray-400">Somente PDF</span>
            </FileUploadButton>
          </div>
        )}
        {errors.regulations && (
          <p className="mt-1.5 text-sm text-red-500">
            {errors.regulations.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
